import React, {useEffect, useReducer, useState} from 'react'
import {
  StyledWorkspaceContainer as Container,
  StyledWorkspaceFilter as Filter,
  StyledWorkspaceFloorList as FloorListContainer,
  StyledWorkspaceFloorPlan as FloorPlanContainer,
  StyledWorkspaceHeader as WorkspaceHeader
} from '../../containers/Workspace/StyledWorkspace'
import Header from '../../components/Header/Header'
import Clock from '../../components/Clock/Clock'
import HeaderTitle from '../../components/Header/HeaderTitle'
import {_L, useLocaleState} from '../../context/LocaleContext'
import isEmpty from 'lodash/isEmpty'
import HeaderButton from '../../components/Header/HeaderButton'
import MediaQuery from 'react-responsive'
import FloorList, {DIRECTION} from '../../components/FloorList/FloorList'
import {fetchBuilding, fetchBuildingFloors} from '../../utils/buildings'
import {useUIDispatch, useUIState} from '../../context/UIContext'
import FloorPlan from '../../containers/FloorPlan/FloorPlan'
import {
  setDrawingHighlights,
  useDrawingDispatch
} from '../../context/DrawingContext'
import BuildingList from '../../containers/BuildingList/BuildingList'
import {
  fetchRoomsByDepartment,
  fetchRoomTypesToHighlight,
  fetchRoomWithEmployees
} from '../../api/room'
import RoomSelectMiniFilter from './Filter/RoomSelectMiniFilter'
import RoomSelectFilter from './Filter/RoomSelectFilter'
import {Layer} from 'grommet'
import styled from 'styled-components'
import {fetchLocationEmployees} from '../../api/employees'
import {fetchFloor} from '../../api/floor'
import {resetHighlights} from '../../utils/highlight'
import RoomSelectDetail from './Detail/RoomSelectDetail'

const ModalFilter = ({
  employees,
  clearSelectedEmployee,
  roomTypesByFloor,
  departmentsByFloor,
  onActiveFilterList,
  onSelectFilterItem,
  onCloseFilter,
  responsive,
  ...rest
}) => (
  <Layer responsive={responsive} {...rest}>
    <FilterContainer>
      <RoomSelectFilter
        employees={employees}
        clearSelectedEmployee={clearSelectedEmployee}
        roomTypesByFloor={roomTypesByFloor}
        departmentsByFloor={departmentsByFloor}
        onActiveFilterList={onActiveFilterList}
        onSelectFilterItem={onSelectFilterItem}
        onCloseFilter={onCloseFilter}
        showClose
      />
    </FilterContainer>
  </Layer>
)

const initialState = {
  building: {blId: '', name: ''},
  floors: [],
  employees: [],
  roomTypesByFloor: {},
  departmentsByFloor: {}
}

const reducer = (state, action) => {
  if (action.type === 'setData') {
    return {
      ...state,
      ...action.payload
    }
  } else {
    throw new Error('Unexpected action')
  }
}

const highlightRoom = ({drawingDispatch, blId, flId, rmId, show}) => {
  const assetType = 'rm'
  const selectable = true
  const selector = true
  const roomId = `${blId};${flId};${rmId}`
  let highlights = {}
  highlights[roomId] = {
    stroke: '#3395fE',
    fill: 'rgba(51, 149, 254, 0.38)',
    strokeWidth: '0.3%'
  }
  setDrawingHighlights({
    dispatch: drawingDispatch,
    assetType,
    highlights,
    show,
    selectable,
    selector
  })
}

let showUserLocation = true
let selectedEmployeeOnOtherFloor = {}

const RoomSelect = ({selectedRoomId, onSelectRoom}) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const localeState = useLocaleState()
  const uiState = useUIState()
  const uiDispatch = useUIDispatch()
  const drawingDispatch = useDrawingDispatch()

  const [selectedRoom, setSelectedRoom] = useState({})
  const [showSelectedRoomDetails, setShowSelectedRoomDetails] = useState(false)
  const [showFilter, setShowFilter] = useState(false)

  const [showBuildingList, setShowBuildingList] = useState(false)
  const [highlightAssets, setHighlightAssets] = useState(false)
  const [resetFilterHighlights, setResetFilterHighlights] = useState(false)
  const [clearSelectedEmployee, setClearSelectedEmployee] = useState(false)

  const {
    building,
    floors,
    employees,
    departmentsByFloor,
    roomTypesByFloor
  } = state
  const {activeFloor} = uiState
  const {blId, flId} = activeFloor

  useEffect(
    () => {
      let didCancel = false

      async function fetchData() {
        if (blId) {
          let [building, floors, employees] = await Promise.all(
            [fetchBuilding, fetchBuildingFloors, fetchLocationEmployees].map(
              fn => fn({blId})
            )
          )
          if (!didCancel) {
            resetHighlights({drawingDispatch})
            dispatch({
              type: 'setData',
              payload: {building, floors, employees}
            })
          }
        }
      }

      fetchData()

      return () => {
        didCancel = true
        showUserLocation = true
      }
    },
    [blId, drawingDispatch]
  )

  useEffect(
    () => {
      async function fetchData() {
        let [departmentsByFloor, roomTypesByFloor] = await Promise.all(
          [fetchRoomsByDepartment, fetchRoomTypesToHighlight].map(fn =>
            fn(activeFloor)
          )
        )
        dispatch({
          type: 'setData',
          payload: {departmentsByFloor, roomTypesByFloor}
        })
        setHighlightAssets([
          {
            assetType: 'rm',
            fill: 'rgba(51, 149, 254, 0.38)',
            stroke: '#3395fE',
            strokeWidth: '0.3%',
            selector: true,
            onClick: {
              handler: (assetId, isSelected) => {
                let [blId, flId, rmId] = assetId.split(';')
                selectRoom({
                  drawingDispatch,
                  blId,
                  flId,
                  rmId,
                  show: !isSelected
                })
              }
            }
          }
        ])
        // select user room
        if (selectedRoomId && showUserLocation) {
          const {blId, flId} = activeFloor
          selectRoom({
            drawingDispatch,
            blId,
            flId,
            rmId: selectedRoomId,
            show: true
          })
          showUserLocation = false
        } else if (!isEmpty(selectedEmployeeOnOtherFloor)) {
          // select employee on other floor
          selectRoom({
            drawingDispatch,
            ...selectedEmployeeOnOtherFloor,
            show: true
          })
          selectedEmployeeOnOtherFloor = {}
        }
      }

      fetchData()

      return () => {
        setHighlightAssets([])
        resetHighlights({drawingDispatch})
      }
    },
    [activeFloor, drawingDispatch, selectedRoomId]
  )

  const selectRoom = ({drawingDispatch, blId, flId, rmId, show}) => {
    highlightRoom({drawingDispatch, blId, flId, rmId, show})
    if (show) {
      const room = {blId, flId, rmId}
      showRoomDetails({room})
    } else {
      setShowSelectedRoomDetails(false)
    }
  }

  const showRoomDetails = async ({room}) => {
    const roomData = await fetchRoomWithEmployees(room)
    setSelectedRoom(roomData)
    setShowSelectedRoomDetails(true)
  }

  const onSelectLocation = () => {
    onSelectRoom({room: selectedRoom})
  }

  const onCancelSelectLocation = () => {
    onSelectRoom({room: null})
  }

  const onFloorChange = ({bl_id, fl_id}) => {
    // hide room details
    setShowSelectedRoomDetails(false)
    uiDispatch({
      type: 'setActiveFloor',
      payload: {
        blId: bl_id,
        flId: fl_id
      }
    })
  }

  const onBuildingSelect = async blId => {
    const flId = await fetchFloor({blId})
    if (flId) {
      uiDispatch({type: 'setActiveFloor', payload: {blId, flId}})
      setShowBuildingList(false)
      setShowFilter(false)
      setShowSelectedRoomDetails(false)
    } else {
      // TODO add nice message
      alert('Building without floors')
    }
  }

  const onBuildingClick = () => {
    setShowBuildingList(true)
    setShowFilter(false)
    setShowSelectedRoomDetails(false)
  }

  const onActiveFilterList = () => {
    setResetFilterHighlights(true)
  }

  const onSelectFilterItem = ({type, item}) => {
    switch (type) {
      case 'em':
        onClickEmployee(item)
        break
      case 'rm':
        onClickRoom(item)
        break
      case 'rmtype':
        onClickRoomType(item)
        break
      case 'dp':
        onClickDepartment(item)
        break
      default:
        throw new Error('Unexpected item selected')
    }
  }

  const onClickEmployee = ({employee}) => {
    setClearSelectedEmployee(false)
    const {blId, flId: employeeFloor} = employee
    if (employeeFloor !== flId) {
      selectedEmployeeOnOtherFloor = {...employee}
      onFloorChange({bl_id: blId, fl_id: employeeFloor})
    } else {
      selectRoom({
        drawingDispatch,
        ...employee,
        show: true
      })
    }
  }

  const onClickRoom = ({blId, flId, rmId}) => {
    selectRoom({
      drawingDispatch,
      blId,
      flId,
      rmId,
      show: true
    })
  }

  const onClickRoomType = ({rmType, show}) => {
    const assetType = 'rm'
    const {color, rooms} = roomTypesByFloor[rmType]
    let highlights = {}
    rooms.forEach(room => {
      highlights[room] = {
        stroke: color.text,
        fill: color.button,
        strokeWidth: '0.3%'
      }
    })
    if (resetFilterHighlights) {
      resetDrawingHighlights('dp').then(() => {
        setDrawingHighlights({
          dispatch: drawingDispatch,
          assetType,
          highlights,
          show
        })
      })
      setResetFilterHighlights(false)
    } else {
      setDrawingHighlights({
        dispatch: drawingDispatch,
        assetType,
        highlights,
        show
      })
    }
  }

  const onClickDepartment = ({dv_id, dp_id, show}) => {
    const assetType = 'rm'
    const departmentCode = dv_id + '|' + dp_id
    const {color, rooms} = departmentsByFloor[departmentCode]
    let highlights = {}
    rooms.forEach(room => {
      highlights[room] = {
        stroke: color.text,
        fill: color.button,
        strokeWidth: '0.3%'
      }
    })
    if (resetFilterHighlights) {
      resetDrawingHighlights('rmtype').then(() => {
        setDrawingHighlights({
          dispatch: drawingDispatch,
          assetType,
          highlights,
          show
        })
      })
      setResetFilterHighlights(false)
    } else {
      setDrawingHighlights({
        dispatch: drawingDispatch,
        assetType,
        highlights,
        show
      })
    }
  }

  /**
   * This fn is async because it needs to return a Promise to highlight assets
   * after the reset highlight is done
   * @param highlight Highlight type: rmtype|dp
   * @returns {Promise<void>}
   */
  const resetDrawingHighlights = async highlight => {
    if (!highlight) {
      return
    }
    let floorElements = {}
    switch (highlight) {
      case 'rmtype':
        floorElements = {...roomTypesByFloor}
        break
      case 'dp':
        floorElements = {...departmentsByFloor}
    }
    // reset highlights
    let highlights = {}
    Object.values(floorElements).map(floorElement => {
      const {rooms} = floorElement
      rooms.forEach(room => {
        highlights[room] = {}
      })
    })
    setDrawingHighlights({
      dispatch: drawingDispatch,
      assetType: 'rm',
      highlights,
      show: false
    })
  }

  const onShowFilter = () => {
    setShowFilter(true)
    setResetFilterHighlights(true)
  }

  const onCloseFilter = () => setShowFilter(false)

  const onCloseRoomDetails = () => setShowSelectedRoomDetails(false)

  const dotChar = String.fromCharCode(183)
  const title = `${building.name} ${dotChar} ${_L({
    key: 'Floor ',
    localeState
  })} ${flId}`

  return (
    <Container>
      <WorkspaceHeader>
        <Header
          leftContent={<Clock />}
          centerContent={
            <HeaderTitle title={_L({key: 'Select Location', localeState})} />
          }
          rightContent={
            <HeaderButton
              onClick={onCancelSelectLocation}
              label={_L({key: 'Cancel', localeState})}
            />
          }
        />
      </WorkspaceHeader>
      {showBuildingList ? (
        <BuildingList
          onBuildingSelect={onBuildingSelect}
          showNotification={false}
        />
      ) : (
        <FloorListContainer>
          <MediaQuery maxWidth={768}>
            {matches => (
              <FloorList
                selectedFloor={activeFloor}
                buildingCode={building.blId}
                floorList={floors}
                direction={matches ? DIRECTION.HORIZONTAL : DIRECTION.VERTICAL}
                onSelected={({bl_id, fl_id}) => {
                  setClearSelectedEmployee(true)
                  onFloorChange({bl_id, fl_id})
                }}
                availableOnly={false}
                showBuilding
                onBuildingClick={onBuildingClick}
              />
            )}
          </MediaQuery>
        </FloorListContainer>
      )}
      {!showBuildingList && (
        <Filter>
          <MediaQuery maxWidth={768}>
            {matches => {
              if (matches) {
                return <RoomSelectMiniFilter onShowFilter={onShowFilter} />
              } else {
                return (
                  <RoomSelectFilter
                    employees={employees}
                    clearSelectedEmployee={clearSelectedEmployee}
                    roomTypesByFloor={roomTypesByFloor}
                    departmentsByFloor={departmentsByFloor}
                    onActiveFilterList={onActiveFilterList}
                    onSelectFilterItem={onSelectFilterItem}
                  />
                )
              }
            }}
          </MediaQuery>
        </Filter>
      )}
      {showSelectedRoomDetails && (
        <RoomSelectDetail
          room={selectedRoom}
          onSelectLocation={onSelectLocation}
          onClose={onCloseRoomDetails}
        />
      )}
      {!showBuildingList && (
        <FloorPlanContainer>
          <FloorPlan
            parentView="ROOM_SELECT"
            showDepartmentList={false}
            title={title}
            highlightAssets={highlightAssets}
          />
        </FloorPlanContainer>
      )}
      {showFilter && (
        <MediaQuery maxDeviceWidth={420}>
          {matches => (
            <ModalFilter
              employees={employees}
              clearSelectedEmployee={clearSelectedEmployee}
              roomTypesByFloor={roomTypesByFloor}
              departmentsByFloor={departmentsByFloor}
              onActiveFilterList={onActiveFilterList}
              onSelectFilterItem={onSelectFilterItem}
              onCloseFilter={onCloseFilter}
              responsive={matches}
            />
          )}
        </MediaQuery>
      )}
    </Container>
  )
}

export default RoomSelect

const FilterContainer = styled.div`
  width: 90vw;
  @media (max-width: 420px) {
    height: 100%;
    width: 100%;
  }
`
